import React from 'react'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {ShieldCheck, Server, Zap, Settings, CheckCircle, Download} from 'lucide-react'

export default function CpanelPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            <span>Official Licensed Module</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            WHMCS cPanel Integration Module
          </h1>
          
          <p className="text-xl text-slate-600 mb-8">
            Complete automation between WHMCS and cPanel. Handle account provisioning, suspension, termination, and resource management seamlessly.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Purchase Now - $89
            </button>
            <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all">
              View Documentation
            </button>
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
            <Download className="w-4 h-4" />
            <span>Instant download • Priority support included</span>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Powerful Automation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Server className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Auto Provisioning</h3>
              <p className="text-slate-600">
                Automatically create cPanel accounts when customers order hosting packages. No manual intervention.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Real-time Sync</h3>
              <p className="text-slate-600">
                Suspensions, unsuspensions, and terminations sync instantly between WHMCS and cPanel servers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Resource Control</h3>
              <p className="text-slate-600">
                Manage disk space, bandwidth, email accounts, and databases directly from WHMCS admin panel.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Secure API</h3>
              <p className="text-slate-600">
                Uses official cPanel API with secure token authentication. No stored passwords required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features List */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Complete Feature Set
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Automatic account creation on order completion',
              'Username and password auto-generation',
              'Custom package resource allocation',
              'Automatic suspension on payment failure',
              'Automatic unsuspension on payment received',
              'Account termination and cleanup',
              'Email account management integration',
              'Database creation and limits',
              'FTP account management',
              'Subdomain and addon domain control',
              'SSL certificate management',
              'WHM multi-server support'
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg border border-gray-200">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Why Choose Our cPanel Module?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Save Hours Weekly</h3>
              <p className="text-slate-600">
                Eliminate manual account setup. Automation handles everything from provisioning to termination.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Reduce Errors</h3>
              <p className="text-slate-600">
                No more typos or forgotten configurations. Consistent, error-free account creation every time.
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-gray-200">
              <CheckCircle className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Scale Your Business</h3>
              <p className="text-slate-600">
                Handle hundreds of orders without additional staff. Focus on growth, not manual tasks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Automate Your Hosting Business Today
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hosting providers who save hours every week with our cPanel integration.
          </p>
          <button className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
            Purchase Now - $89
          </button>
        </div>
      </section>

      <Footer />
    </div>
  )
}
